import { cn } from '@/utils/classname';

/**
 * TableControlsContainer - Visual wrapper for table filters and selection bar
 *
 * Provides unified purple container styling for:
 * - Row 1: Filters (left) + Search (right)
 * - Row 2: Actions (left) + Selection info (right)
 *
 * Maintains separation of concerns - this is a presentational layout component only.
 * TableSelectionBar remains focused on selection/actions logic.
 *
 * @example
 * ```tsx
 * <TableControlsContainer
 *   filters={
 *     <>
 *       <FilterDropdown id="status" ... />
 *       <FilterDropdown id="type" ... />
 *       {hasActiveFilters && <Button label="Clear Filters" ... />}
 *     </>
 *   }
 *   filtersRight={
 *     <SearchBar placeholder="Search" ... />
 *   }
 *   selectionBar={
 *     <TableSelectionBar
 *       embedded={true}
 *       alwaysVisible={true}
 *       actions={<BulkActions />}
 *     />
 *   }
 * />
 * ```
 */
export interface TableControlsContainerProps {
  /**
   * Filter dropdowns to render in Row 1 (left side)
   * Typically FilterDropdown components with optional Clear Filters button
   */
  filters: React.ReactNode;

  /**
   * Optional content for Row 1 right side (typically search)
   * Positioned at the end of the filters row
   */
  filtersRight?: React.ReactNode;

  /**
   * TableSelectionBar component to render in Row 2
   * Should be passed with embedded={true} to remove duplicate styling
   */
  selectionBar: React.ReactNode;

  /**
   * Show "Filters:" label before filter dropdowns
   * - true: Shows default "Filters:" label
   * - string: Shows custom label text
   * - false/undefined: No label (default, backward compatible)
   * Matches "Actions:" label styling from TableActionsBar
   * @default false
   */
  filterLabel?: boolean | string;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Test ID for E2E testing
   * @default 'table-controls-container'
   */
  'data-testid'?: string;
}

/**
 * TableControlsContainer Component
 *
 * A layout wrapper that visually unifies table filters and selection bar
 * into a single styled container with consistent theming.
 */
export function TableControlsContainer({
  filters,
  filtersRight,
  selectionBar,
  className,
  filterLabel = false,
  'data-testid': dataTestId = 'table-controls-container',
}: TableControlsContainerProps) {
  return (
    <div
      className={cn(
        'border-chariot-stroke bg-chariot-background-secondary mb-4 rounded-md border',
        className
      )}
      data-testid={dataTestId}
    >
      {/* Row 1: Filters + Search */}
      <div
        className="flex items-start justify-between gap-2 px-4 py-3"
        data-testid={`${dataTestId}-filters`}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {filterLabel && (
            <h2
              className="shrink-0 text-sm font-bold"
              data-testid={`${dataTestId}-filters-label`}
            >
              {typeof filterLabel === 'string' ? filterLabel : 'Filters:'}
            </h2>
          )}
          {filters}
        </div>
        {filtersRight && (
          <div className="shrink-0" data-testid={`${dataTestId}-filters-right`}>
            {filtersRight}
          </div>
        )}
      </div>

      {/* Divider between rows */}
      <div className="border-chariot-stroke border-t" />

      {/* Row 2: Selection bar (renders inside container) */}
      <div className="px-0" data-testid={`${dataTestId}-selection`}>
        {selectionBar}
      </div>
    </div>
  );
}
